/**
 * MarsMate Animation System — Global Timing Constants
 * Import from this file in every animated component.
 * Never hardcode durations or easings elsewhere.
 *
 * Usage:
 *   import { DURATION, EASE, STAGGER, useReducedMotion } from '../animations/timing';
 */

import { useEffect, useState } from 'react';

// ─────────────────────────────────────────────
// DURATIONS (in seconds for Framer Motion, ms for GSAP)
// ─────────────────────────────────────────────
export const DURATION = {
  instant:  0.10,
  fast:     0.20,
  normal:   0.35,
  slow:     0.50,
  verySlow: 0.80,
  page:     0.45,
};

// GSAP equivalents (milliseconds)
export const DURATION_MS = {
  instant:  100,
  fast:     200,
  normal:   350,
  slow:     500,
  verySlow: 800,
  page:     450,
};

// ─────────────────────────────────────────────
// EASING CURVES
// ─────────────────────────────────────────────
// Framer Motion format: [x1, y1, x2, y2] cubic-bezier
export const EASE = {
  spring:    [0.22, 1,    0.36, 1],    // fast-in, decelerate — primary
  easeIn:    [0.4,  0,    1,    1],    // accelerate
  easeOut:   [0,    0,    0.2,  1],    // decelerate
  easeInOut: [0.4,  0,    0.2,  1],    // symmetric
  bounce:    [0.68, -0.55, 0.27, 1.55],// overshoot
  sharp:     [0.4,  0,    0.6,  1],    // snappy cut
};

// GSAP format strings
export const EASE_GSAP = {
  spring:    'power3.out',
  easeIn:    'power2.in',
  easeOut:   'power2.out',
  easeInOut: 'power2.inOut',
  bounce:    'back.out(1.7)',
  elastic:   'elastic.out(1, 0.5)',
};

// ─────────────────────────────────────────────
// STAGGER VALUES (seconds)
// ─────────────────────────────────────────────
export const STAGGER = {
  fast:   0.05,
  normal: 0.08,
  slow:   0.12,
  cards:  0.10,
};

// ─────────────────────────────────────────────
// SPRING CONFIGS (Framer Motion)
// ─────────────────────────────────────────────
export const SPRING = {
  snappy: { type: 'spring', stiffness: 500, damping: 30 },
  bouncy: { type: 'spring', stiffness: 400, damping: 17 },
  gentle: { type: 'spring', stiffness: 200, damping: 25 },
  slow:   { type: 'spring', stiffness: 100, damping: 20 },
};

// ─────────────────────────────────────────────
// REUSABLE FRAMER MOTION VARIANTS
// ─────────────────────────────────────────────
export const VARIANTS = {
  fadeUp: {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: DURATION.normal, ease: EASE.spring },
    },
  },
  fadeDown: {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: DURATION.normal, ease: EASE.spring },
    },
  },
  fadeLeft: {
    hidden: { opacity: 0, x: -24 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: DURATION.normal, ease: EASE.spring },
    },
  },
  fadeRight: {
    hidden: { opacity: 0, x: 24 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: DURATION.normal, ease: EASE.spring },
    },
  },
  scaleIn: {
    hidden: { opacity: 0, scale: 0.88 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: DURATION.normal, ease: EASE.spring },
    },
  },
  blurIn: {
    hidden: { opacity: 0, filter: 'blur(12px)', y: 10 },
    visible: {
      opacity: 1,
      filter: 'blur(0px)',
      y: 0,
      transition: { duration: DURATION.slow, ease: EASE.spring },
    },
  },
  staggerContainer: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: STAGGER.normal, delayChildren: 0.1 },
    },
  },
  staggerItem: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: DURATION.normal, ease: EASE.spring },
    },
  },
};

// Page-level transition variants
export const PAGE_VARIANTS = {
  public: {
    initial: { opacity: 0, y: 12, filter: 'blur(6px)' },
    enter: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: { duration: DURATION.page, ease: EASE.spring },
    },
    exit: {
      opacity: 0,
      y: -10,
      filter: 'blur(4px)',
      transition: { duration: DURATION.fast, ease: EASE.easeIn },
    },
  },
  dashboard: {
    initial: { opacity: 0, x: 16 },
    enter: {
      opacity: 1,
      x: 0,
      transition: { duration: DURATION.normal, ease: EASE.spring },
    },
    exit: {
      opacity: 0,
      x: -16,
      transition: { duration: DURATION.fast, ease: EASE.easeIn },
    },
  },
};

// ─────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────
// Mars palette (matches styles/tokens.css). Kit components read these.
export const COLORS = {
  bg: {
    page:     '#050505',
    card:     '#121212',
    elevated: '#1C1C1C',
    glass:    'rgba(255, 255, 255, 0.05)',
  },
  brand: {
    indigo: '#C1440E',   // primary (Mars Red) — key kept for compat
    violet: '#E5622D',   // lighter mars
    cyan:   '#F5A623',   // amber accent
    primary: '#C1440E',
    gradient: 'linear-gradient(135deg, #C1440E 0%, #E5622D 55%, #F5A623 100%)',
  },
  text: {
    primary:   '#F5F5F5',
    secondary: '#D4D4D4',
    muted:     '#A3A3A3',
  },
  border: {
    default: '#262626',
    hover:   'rgba(193, 68, 14, 0.40)',
    active:  'rgba(193, 68, 14, 0.55)',
  },
  glow: {
    indigo: 'rgba(193, 68, 14, 0.45)',   // mars glow (key kept for compat)
    violet: 'rgba(229, 98, 45, 0.40)',
    cyan:   'rgba(245, 166, 35, 0.40)',
  },
};

// ─────────────────────────────────────────────
// useReducedMotion — respects prefers-reduced-motion
// ─────────────────────────────────────────────
export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduced;
}

// ─────────────────────────────────────────────
// Helper: safe animation props (no-op if reduced)
// ─────────────────────────────────────────────
export function safeMotion(reducedMotion, variants, fallback = {}) {
  if (reducedMotion) return fallback;
  return variants;
}
